import { useState, useEffect } from "react";
import { Form, useNavigate, useSearchParams } from "react-router-dom";
import { createNewFiling } from "../../api/filings/filingAPI";
import NotificationAlert from "../ui/alerts/notifications/NotificationAlert";
import { Handler401 } from "../../api/utils/responseActions";
import FormSectionWrapper from "../ui/account/FormSectionWrapper";

function CreateFilingForm({ allowServices, onSubmit, filingData }) {
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get("mode") === "DE";
  const hasPersonalNewFiling = allowServices.some(
    (service) => service.type === "PTF"
  );
  const hasBusinessNewFiling = allowServices.some(
    (service) => service.type === "BTF"
  );
  const hasNewPlanning = allowServices.some(
    (service) => service.type === "TPC"
  );

  // Initialize selectedOptions based on filingData if it exists
  const getInitialSelectedOptions = () => {
    if (!filingData?.tax_service?.services) {
      return {
        personalTax: false,
        businessTax: false,
        planningTax: false,
      };
    }

    const services = filingData.tax_service.services;
    return {
      personalTax: services.includes("PTF"),
      businessTax: services.includes("BTF"),
      planningTax: services.includes("TPC"),
    };
  };

  const [selectedOptions, setSelectedOptions] = useState(
    getInitialSelectedOptions()
  );

  // Update selectedOptions when filingData changes
  useEffect(() => {
    setSelectedOptions(getInitialSelectedOptions());
  }, [filingData]);

  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);

    const apiUrl = "tax/filing";
    let taxServices = [];

    if (formData.get("personalTax")) {
      taxServices.push("PTF");
    }
    if (formData.get("businessTax")) {
      taxServices.push("BTF");
    }
    if (formData.get("planningTax")) {
      taxServices.push("TPC");
    }

    const data = {
      tax_service: { services: taxServices },
      mode: isDemo ? "DE" : "RE",
    };

    try {
      const response = await createNewFiling(apiUrl, data);
      const restData = await response.json();

      if (
        response.status === 400 &&
        "operation" in restData &&
        restData.operation === "i"
      ) {
        navigate("?drafts=exceed");
        return;
      }

      if (!response.ok) {
        if (response.status === 401) {
          Handler401(navigate);
          return;
        }
        throw new Error("Need to handle this errors, probably 500");
      }

      // Call the onSubmit prop with the new filing data
      onSubmit({
        filingUuid: restData.filingUuid,
        ...data,
      });

      navigate(`${restData.filingUuid}`);
    } catch (error) {
      setError("You must select at least one tax service.");
      console.log(error);
    }
  };

  const handlerOptionChange = (event) => {
    const { name, checked } = event.target;
    setSelectedOptions((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  };

  return (
    <div className="columns">
      {error && (
        <NotificationAlert
          alertType="error"
          alertTitle="Invalid Selection"
          alertDescription={error}
        />
      )}
      <Form method="post" onSubmit={handleSubmit}>
        <FormSectionWrapper
          title="Filing Type"
          infoTooltip="Select whether you’re filing personal taxes, business taxes, or both."
        >
          <div className="form-field">
            <div className="checkbox-container">
              {hasPersonalNewFiling && (
                <div className="checkbox-item">
                  <div className="checkbox-representation">
                    <input
                      type="checkbox"
                      name="personalTax"
                      value="PersonalTax"
                      id="checkbox1"
                      checked={selectedOptions.personalTax}
                      onChange={handlerOptionChange}
                    />
                    <label htmlFor="checkbox1">Personal filing</label>
                  </div>
                </div>
              )}

              {hasBusinessNewFiling && (
                <div className="checkbox-item">
                  <div className="checkbox-representation">
                    <input
                      type="checkbox"
                      name="businessTax"
                      value="BusinessTax"
                      id="checkbox2"
                      checked={selectedOptions.businessTax}
                      onChange={handlerOptionChange}
                    />
                    <label htmlFor="checkbox2">Business or trust iling</label>
                  </div>
                </div>
              )}

              {hasNewPlanning && (
                <div className="checkbox-item">
                  <div className="checkbox-representation">
                    <input
                      type="checkbox"
                      name="planningTax"
                      value="TaxPlanning"
                      id="checkbox3"
                      checked={selectedOptions.planningTax}
                      onChange={handlerOptionChange}
                    />
                    <label htmlFor="checkbox3">Tax planning/consulting</label>
                  </div>
                </div>
              )}
            </div>
          </div>
        </FormSectionWrapper>
        <div className="prevNext">
          <button className="cta-button primary" data-icon="arrow_forward">
            <b>Next</b>
          </button>
        </div>
      </Form>
    </div>
  );
}

export default CreateFilingForm;
